// KinFlex — 30 SEO-ready service pages
// Each entry has ALL SEO sections needed for a dedicated service page.

export const SERVICES = [
  {
    slug: "home-nursing",
    title: "Home Nursing Services",
    category: "nursing",
    icon: "Stethoscope",
    short: "Skilled GNM/BSc-qualified nurses providing round-the-clock nursing at home.",
    hero: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Home Nursing Services in Delhi NCR | KinFlex Home Healthcare",
    metaDescription: "Book verified home nursing in Delhi NCR — GNM/BSc qualified nurses for 12h/24h shifts. Medication, vitals, wound care & family updates. Same-day nurse.",
    intro: "Home Nursing brings hospital-grade care into the safety and comfort of home. Our nurses are qualified GNMs and BSc Nursing professionals who handle medication schedules, vitals, injections, wound care, and daily patient routines with warmth and precision. From short recovery windows to long-term care, we build a schedule that fits your family — and we stay accountable through clinical supervision every step of the way.",
    whoNeeds: [
      "Elderly patients living alone or with working children",
      "Post-surgery patients needing dressings and monitoring at home",
      "Chronic illness patients (diabetes, heart, kidney, respiratory)",
      "Post-hospital discharge cases where hospital-to-home transition is needed",
      "Bedridden or semi-bedridden patients requiring daily care",
    ],
    benefits: [
      "Skilled clinical care without hospital admission stress",
      "One-to-one attention every single shift",
      "Lower risk of hospital-acquired infections",
      "Family peace of mind with daily written updates",
      "Cost-effective compared to long hospital stays",
    ],
    situations: [
      "Blood pressure or diabetes management at home",
      "Wound dressing after surgery",
      "Ryle's tube feeding schedules",
      "Injectable medication (insulin, antibiotics)",
      "Vitals monitoring and doctor coordination",
    ],
    howWeHelp: "Every KinFlex nurse is background-verified, medically certified, and continuously trained. A clinical supervisor reviews each case, matches the right nurse to your patient, and stays involved through the care journey. Nurses maintain daily care logs, coordinate with the treating doctor, and escalate anything that matters — never leaving families in the dark.",
    process: [
      { step: "01", title: "Call Us", desc: "Reach us on phone, WhatsApp or the booking form — 24×7." },
      { step: "02", title: "Assessment", desc: "Our clinical team assesses needs, medical history & care plan." },
      { step: "03", title: "Nurse Assigned", desc: "The right-fit nurse is matched, briefed & deployed to your home." },
      { step: "04", title: "Care Begins", desc: "Care starts at home with daily updates & ongoing supervision." },
    ],
    whyChoose: ["Verified GNM / BSc qualified nurses", "24×7 shifts + supervision", "Transparent pricing", "Same-day deployment across Delhi NCR", "Family updates every shift"],
    coverage: "Available across Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "What qualifications do KinFlex nurses have?", a: "All KinFlex nurses are GNM or BSc Nursing qualified, with hospital experience and background verification." },
      { q: "Can I book a 12-hour shift instead of 24 hours?", a: "Yes. We offer 8h, 12h and 24h shifts. Longer bookings unlock lower per-hour pricing." },
      { q: "Do you handle injections and IV lines at home?", a: "Yes — insulin, antibiotics, IV fluids and other prescribed injections are safely administered by our nurses." },
    ],
    related: ["critical-care-nurse", "elder-care", "post-surgical-care", "bedridden-patient-care"],
    features: ["Medication administration", "Vitals monitoring", "Wound care & dressings", "IV & injections", "Family updates"],
    who: "Ideal for elderly, post-surgery, chronic illness, and bedridden patients.",
    description: "Skilled home nursing with verified professionals — for daytime, nighttime or 24×7 shifts across Delhi NCR.",
  },
  {
    slug: "critical-care-nurse",
    title: "Critical Care Nurse at Home",
    category: "critical",
    icon: "Activity",
    short: "ICU-trained nurses for ventilator, coma, paralysis, cancer & trauma patients.",
    hero: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Critical Care Nurse at Home in Delhi NCR | ICU-Trained Nurses — KinFlex",
    metaDescription: "ICU-trained critical care nurses at home — ventilator, tracheostomy, coma, paralysis, cancer & trauma care in Delhi NCR. 24×7 rapid deployment.",
    intro: "Critical Care Nursing at home means bringing ICU-level clinical skill to your living room. Our critical care nurses have hospital ICU experience and can manage ventilators, tracheostomies, IV medications, vitals, and complex clinical protocols with confidence. They work under doctor supervision and follow strict safety and infection-control standards — helping families avoid long, distressing hospital stays.",
    whoNeeds: [
      "ICU discharge patients needing continued ventilator or monitoring support",
      "Coma and semi-conscious patients",
      "Paralysis and stroke patients",
      "Cancer patients in advanced or palliative stages",
      "Post-accident and trauma recovery patients",
    ],
    benefits: [
      "Hospital-grade care without hospital costs",
      "Reduced infection risk versus long ICU stays",
      "Familiar home environment aids recovery",
      "Better sleep, nutrition and emotional support",
      "Family involvement in care planning",
    ],
    situations: [
      "Ventilator settings and suction management",
      "Tracheostomy tube cleaning and change",
      "IV medication and blood pressure control",
      "Ryle's tube feeding for unconscious patients",
      "Wound care for pressure sores or surgical sites",
    ],
    howWeHelp: "Every critical care case is reviewed by our clinical lead before deployment. We match nurses with ICU experience relevant to the patient's condition — respiratory, neuro, cardiac or oncology — and coordinate directly with the treating doctor. A 24×7 supervisor stays on-call throughout the care period.",
    process: [
      { step: "01", title: "Call Us", desc: "Reach us any time — we prioritise critical care cases." },
      { step: "02", title: "Clinical Review", desc: "Our clinical lead reviews the case with the treating doctor." },
      { step: "03", title: "ICU-Trained Nurse Deployed", desc: "The matched nurse reaches home within hours." },
      { step: "04", title: "Continuous Care", desc: "Round-the-clock nursing with doctor coordination and family updates." },
    ],
    whyChoose: ["ICU-experienced nurses only", "24×7 on-call clinical supervisor", "Same-day deployment", "Doctor coordination included", "Full infection-control protocols"],
    coverage: "Rapid deployment across Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Can KinFlex nurses manage a ventilator at home?", a: "Yes. Our critical care nurses are trained on portable ICU ventilators, humidifiers, and suction. We also set up the equipment if needed." },
      { q: "How fast can a critical care nurse reach home?", a: "For urgent cases across Delhi NCR, deployment is within 2–4 hours. In rare peak times, up to 6 hours." },
      { q: "Do you coordinate with our treating doctor?", a: "Absolutely. We share vitals logs, medication charts and status updates with the treating doctor as required." },
    ],
    related: ["icu-setup-at-home", "ventilator-care", "tracheostomy-care", "post-surgical-care"],
    features: ["ICU-level nursing", "Ventilator & tracheostomy care", "Multi-parameter monitoring", "Coma & neuro care", "Palliative & trauma care"],
    who: "For patients discharged from ICU or needing critical monitoring at home.",
    description: "ICU-grade nursing brought to your home — ventilator, tracheostomy, and complex care by experienced nurses.",
  },
  {
    slug: "icu-setup-at-home",
    title: "ICU Setup at Home",
    category: "critical",
    icon: "MonitorSmartphone",
    short: "Full home ICU — ventilator, monitor, oxygen, suction, ICU bed & trained team.",
    hero: "https://images.unsplash.com/photo-1581056771107-24ca5f033842?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "ICU Setup at Home in Delhi NCR | Ventilator + Nurse | KinFlex",
    metaDescription: "Full ICU setup at home in Delhi NCR — ventilator, ICU bed, monitor, oxygen, suction and critical care nurses on rotation. Same-day setup available.",
    intro: "A KinFlex home ICU brings the equipment, protocols and clinical team of an intensive care unit into your home — usually within a few hours. We handle everything: ICU bed, ventilator, multi-para monitor, oxygen source, suction machine, and a rotating team of critical care nurses under doctor supervision. Families avoid long, expensive hospital stays while patients recover in a familiar, calm environment.",
    whoNeeds: [
      "Long-term ventilator-dependent patients",
      "ICU discharge patients still needing continuous monitoring",
      "Advanced neuro, cardiac, or respiratory illness patients",
      "Post-surgical patients with high complication risk",
      "Palliative care patients where hospital stays worsen quality of life",
    ],
    benefits: [
      "ICU-level care in a comforting home setting",
      "Significant cost savings over ICU stays",
      "Reduced infection risk",
      "Continuous family involvement",
      "Full equipment on rent — no capital purchase needed",
    ],
    situations: [
      "Ventilator-dependent patients requiring long stays",
      "Post-CABG or major surgery recovery",
      "Advanced stroke or brain injury care",
      "End-stage organ failure palliation",
      "Elderly patients preferring to receive care at home",
    ],
    howWeHelp: "Our team visits your home to plan the ICU space, install equipment, and brief the family. A dedicated critical care nurse team then rotates around the clock, with a supervisor and doctor available on call. We handle everything from oxygen refills to equipment servicing.",
    process: [
      { step: "01", title: "Site Assessment", desc: "Home visit to plan power, space and equipment placement." },
      { step: "02", title: "Equipment Setup", desc: "ICU bed, ventilator, monitor, oxygen, suction — installed & tested." },
      { step: "03", title: "Nurse Team Deployed", desc: "Critical care nurses begin 24×7 rotation under supervision." },
      { step: "04", title: "Ongoing Support", desc: "Equipment maintenance, refills and clinical review continue." },
    ],
    whyChoose: ["Complete turnkey ICU setup", "Same-day deployment", "Verified nurses + supervisor + doctor", "Transparent equipment rental", "24×7 support hotline"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How long does home ICU setup take?", a: "Most home ICU setups are complete within 4–8 hours of confirmation, with the nurse team starting immediately." },
      { q: "Is home ICU cheaper than a hospital ICU?", a: "Yes — typically 40–60% lower monthly cost than a private hospital ICU, without compromising on clinical quality." },
      { q: "What if we don't have enough space?", a: "We plan around your home — the ICU can be set up in a single bedroom with basic power and clean space." },
    ],
    related: ["critical-care-nurse", "ventilator-care", "oxygen-therapy", "hospital-bed-rental"],
    features: ["ICU bed + ventilator + monitor", "Oxygen + suction", "24×7 critical care nurses", "Doctor supervision", "Same-day setup"],
    who: "For ICU discharge patients and long-term critical care at home.",
    description: "Complete Intensive Care Unit at home — equipment plus critical care nursing team, deployed within hours.",
  },
  {
    slug: "physiotherapy-at-home",
    title: "Physiotherapy at Home",
    category: "rehab",
    icon: "Move",
    short: "Certified physiotherapists for post-surgery, stroke, orthopaedic & sports recovery.",
    hero: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Physiotherapy at Home in Delhi NCR | BPT/MPT Therapists | KinFlex",
    metaDescription: "Book BPT/MPT physiotherapy at home in Delhi NCR — post-surgery, stroke, ortho, sports & respiratory rehab. Structured programs with measurable progress.",
    intro: "KinFlex's home physiotherapy program combines qualified BPT / MPT therapists with structured, measurable recovery plans. Whether the goal is regaining knee mobility after surgery, rebuilding strength after a stroke, or managing chronic back pain — our therapists design a program specific to your body, your goals, and your home environment. Every session is progress-tracked and shared with the family.",
    whoNeeds: [
      "Post-surgery patients (knee, hip, spine, abdominal)",
      "Stroke and paralysis survivors",
      "Orthopaedic patients (arthritis, frozen shoulder, back pain)",
      "Sports and accident injury patients",
      "Elderly patients needing balance and strength therapy",
    ],
    benefits: [
      "Faster, structured recovery",
      "No travel required to a clinic",
      "One-to-one attention every session",
      "Home-friendly exercise plans",
      "Reduced re-admission risk",
    ],
    situations: [
      "Post knee-replacement rehabilitation",
      "Post-stroke neuro-rehabilitation",
      "Frozen shoulder mobilisation",
      "Chest physiotherapy for respiratory patients",
      "Sports injury recovery",
    ],
    howWeHelp: "Our physiotherapists conduct a home assessment, set measurable goals with the patient and family, and schedule visits at a frequency that fits recovery — typically 3–6 sessions per week. Sessions include manual therapy, mobility exercises, strength training, and home programs. Progress reports are shared regularly.",
    process: [
      { step: "01", title: "Book a Session", desc: "Call or fill the form — we schedule a first assessment." },
      { step: "02", title: "Home Assessment", desc: "Therapist assesses condition, mobility & goals." },
      { step: "03", title: "Personalised Program", desc: "Structured plan with clear milestones is designed." },
      { step: "04", title: "Sessions + Tracking", desc: "Home sessions with progress reports every 2 weeks." },
    ],
    whyChoose: ["BPT / MPT qualified therapists", "Structured, measurable plans", "One-to-one attention", "Home-friendly exercise routines", "Regular progress reports"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How many sessions will I need?", a: "It depends on condition and goals. Most rehab plans span 4–12 weeks with 3–6 sessions per week." },
      { q: "Do you handle post-stroke rehabilitation?", a: "Yes. Our neuro-physiotherapy team specialises in stroke, paralysis and Parkinson's rehab." },
      { q: "Do I need any equipment at home?", a: "Most sessions need only floor space. Any special equipment (bands, balls) is brought by the therapist or advised for purchase." },
    ],
    related: ["stroke-rehabilitation", "paralysis-rehabilitation", "post-surgical-care", "elder-care"],
    features: ["Post-op rehab", "Stroke & neuro rehab", "Ortho & joint therapy", "Sports injury recovery", "Chest & respiratory physio"],
    who: "Post-op, stroke, arthritis, sports injury, and geriatric patients.",
    description: "Structured physiotherapy at home by BPT/MPT professionals — measurable recovery, one session at a time.",
  },
  {
    slug: "elder-care",
    title: "Elder Care Services",
    category: "care",
    icon: "Users",
    short: "Compassionate senior care — companionship, mobility, meals, medication.",
    hero: "https://images.unsplash.com/photo-1758691462477-976f771224d8?crop=entropy&cs=srgb&fm=jpg&q=85",
    metaTitle: "Elder Care Services at Home in Delhi NCR | Trusted Caregivers — KinFlex",
    metaDescription: "Compassionate elder care at home in Delhi NCR — daily hygiene, meals, medication, mobility, companionship. Verified caregivers, 24×7 or shift-based.",
    intro: "Elder care with KinFlex is about more than routines — it's about dignity, warmth and reliability. Our elder caregivers help with daily hygiene, meals, medication reminders, mobility, and gentle companionship. Every caregiver is verified, trained in geriatric care, and supported by a clinical supervisor. Families get peace of mind, and elders get the care they deserve.",
    whoNeeds: [
      "Elderly parents living alone or with working children",
      "Seniors with early dementia or Alzheimer's",
      "Post-fall or post-surgery recovering seniors",
      "Elders with chronic illness needing daily support",
      "Widowed or emotionally vulnerable seniors",
    ],
    benefits: [
      "Consistent daily routine and hygiene",
      "Medication reminders reduce missed doses",
      "Companionship prevents loneliness",
      "Family stays informed via daily updates",
      "Emergency support 24×7",
    ],
    situations: [
      "Diabetes and blood pressure daily management",
      "Post-fall care and gentle physiotherapy support",
      "Meal preparation and feeding assistance",
      "Doctor visit coordination",
      "Emotional and mental support",
    ],
    howWeHelp: "We match caregivers based on the elder's needs, personality and language preference. Caregivers follow a routine planned with the family, coordinate with the treating doctor, and share daily updates. Clinical supervisors periodically review the care plan.",
    process: [
      { step: "01", title: "Family Call", desc: "Understand the elder's routine, needs and preferences." },
      { step: "02", title: "Caregiver Match", desc: "Right-fit caregiver selected by personality & skill." },
      { step: "03", title: "Care Begins", desc: "Daily routines, hygiene, meals & companionship start." },
      { step: "04", title: "Family Updates", desc: "Regular reports keep families involved & informed." },
    ],
    whyChoose: ["Trained geriatric caregivers", "Personality matching, not just skill", "Daily family updates", "24×7 or shift-based", "Clinical supervision"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Can we get a female caregiver for our mother?", a: "Yes. We match caregivers by gender, language and personality per family preference." },
      { q: "Can the caregiver stay overnight?", a: "Yes. 24×7 live-in caregivers are available across Delhi NCR." },
      { q: "What if we're unhappy with the caregiver?", a: "We replace the caregiver quickly, no questions asked. Continuity of care is our responsibility." },
    ],
    related: ["patient-care-attendant", "home-nursing", "physiotherapy-at-home", "doctor-visit-at-home"],
    features: ["Daily hygiene & personal care", "Meals & medication", "Mobility & companionship", "Doctor visit coordination", "24×7 or shift options"],
    who: "Seniors living alone, dementia patients, and elders needing daily assistance.",
    description: "Warm, verified elder care at home with routine hygiene, meals, meds, mobility and companionship.",
  },
  {
    slug: "patient-care-attendant",
    title: "Patient Care Attendant (GDA)",
    category: "care",
    icon: "Users",
    short: "Trained attendants for hygiene, mobility, feeding & bedside patient support.",
    hero: "https://images.pexels.com/photos/5452268/pexels-photo-5452268.jpeg?auto=compress&cs=tinysrgb&h=650",
    metaTitle: "Patient Care Attendant / GDA in Delhi NCR | Verified Bedside Care — KinFlex",
    metaDescription: "Book trained patient care attendants (GDAs) in Delhi NCR for bedside support — hygiene, feeding, mobility, positioning. Same-day placement.",
    intro: "A patient care attendant (also known as GDA — General Duty Assistant) provides essential bedside support: hygiene, feeding, positioning, mobility and gentle companionship. KinFlex GDAs are certified, verified, and trained under the supervision of qualified nurses. They handle the daily routines that families often cannot manage full-time on their own.",
    whoNeeds: [
      "Bedridden or semi-bedridden patients",
      "Post-surgery patients needing bedside help",
      "Chronic illness patients requiring routine care",
      "Elderly patients with limited mobility",
      "Palliative and long-term care patients",
    ],
    benefits: [
      "Reliable daily bedside routines",
      "Reduced risk of pressure sores through positioning",
      "Better nutrition through supervised feeding",
      "Family gets breathing space and better sleep",
      "Affordable alternative to full nursing shifts where nursing skills aren't required",
    ],
    situations: [
      "Sponge bath and personal hygiene",
      "Two-hourly positioning for bedridden patients",
      "Meal preparation and assisted feeding",
      "Diaper changes and bowel / bladder support",
      "Assisting patient movement between bed and chair",
    ],
    howWeHelp: "We match a GDA to your patient's needs — gender, language and experience — and place them within hours. A supervising nurse or coordinator checks in regularly, and any escalation (medical, behavioural) is quickly handled. GDAs work 12h or 24h shifts.",
    process: [
      { step: "01", title: "Requirement Call", desc: "We understand patient needs and family preferences." },
      { step: "02", title: "GDA Match", desc: "Trained, verified GDA is matched and briefed." },
      { step: "03", title: "Deployment", desc: "GDA reaches home for 12h or 24h shifts." },
      { step: "04", title: "Supervision", desc: "Nurse supervisor stays engaged for quality and escalations." },
    ],
    whyChoose: ["Certified & verified attendants", "12h and 24h shifts", "Nursing supervision included", "Same-day deployment", "Easy replacements if needed"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "What is the difference between a GDA and a nurse?", a: "A GDA handles bedside support — hygiene, positioning, feeding, mobility. A nurse handles clinical tasks — medication, injections, wound care, monitoring." },
      { q: "Can a GDA give injections?", a: "No. Any clinical procedure — injections, IV, wound care — is handled by a nurse. GDAs assist with non-clinical routines." },
      { q: "Are GDAs verified?", a: "Yes. All KinFlex GDAs are police-verified, ID-checked and trained under nurse supervision." },
    ],
    related: ["home-nursing", "elder-care", "bedridden-patient-care", "post-surgical-care"],
    features: ["Bedside hygiene & feeding", "Positioning to prevent sores", "Assisted mobility", "Diaper & bowel support", "12h / 24h shifts"],
    who: "Bedridden, post-surgical, chronic illness and elderly patients needing daily bedside help.",
    description: "Trained GDAs for bedside patient support — hygiene, feeding, mobility and daily routines.",
  },
  {
    slug: "tracheostomy-care",
    title: "Tracheostomy Care at Home",
    category: "procedure",
    icon: "Wind",
    short: "Expert tracheostomy suction, cleaning, tube change & respiratory care.",
    hero: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Tracheostomy Care at Home in Delhi NCR | Trained Nurses — KinFlex",
    metaDescription: "Home tracheostomy care by ICU-trained nurses — suction, cleaning, tube change, humidification & stoma-site care in Delhi NCR. 24×7 support.",
    intro: "Tracheostomy care at home requires clinical precision and strict sterility. KinFlex critical care nurses handle suction, cleaning, tube changes, humidification, and stoma-site care — reducing infection risks and keeping the airway safe. We also train families on emergency response so nothing catches them off-guard.",
    whoNeeds: [
      "Patients with permanent or temporary tracheostomy tubes",
      "Long-term ventilator-dependent patients",
      "Post-surgical head, neck or larynx patients",
      "Patients unable to swallow safely",
      "Advanced neurological patients",
    ],
    benefits: [
      "Reduced infection and blockage risk",
      "Trained emergency response at home",
      "Better sleep and comfort for patient",
      "Family training on safety basics",
      "Doctor coordination on tube changes",
    ],
    situations: [
      "Regular sterile suction",
      "Inner cannula cleaning",
      "Full tube change per protocol",
      "Stoma-site dressing and skin care",
      "Humidifier and oxygen setup",
    ],
    howWeHelp: "A critical care nurse manages the trach care schedule — usually 4–6 hourly suction, daily cleaning, and tube change per protocol. Any warning signs (bleeding, blocked tube, fever) are immediately escalated to the treating doctor. Family members receive basic training too.",
    process: [
      { step: "01", title: "Home Assessment", desc: "Nurse reviews current trach care, tube type and schedule." },
      { step: "02", title: "Care Plan", desc: "Personalised care plan with cleaning and change schedule." },
      { step: "03", title: "Ongoing Care", desc: "Sterile suction, cleaning and tube change per protocol." },
      { step: "04", title: "Family Training", desc: "Basic safety training for the family caregiver." },
    ],
    whyChoose: ["ICU-experienced trach care nurses", "Sterile technique protocols", "24×7 emergency support", "Family training included", "Doctor coordination"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should a tracheostomy tube be changed?", a: "Typically every 4–8 weeks, or per the treating ENT / pulmonologist's advice." },
      { q: "Can family members do the suction themselves?", a: "Yes — we train family caregivers on emergency suction. Sterile daily suction and tube change should be done by a nurse." },
      { q: "What if the tube gets blocked at night?", a: "Our nurses are trained on emergency tube management, and a supervisor is on call 24×7 for guidance." },
    ],
    related: ["critical-care-nurse", "ventilator-care", "oxygen-therapy", "icu-setup-at-home"],
    features: ["Sterile suction", "Tube change per protocol", "Stoma-site care", "Humidifier & oxygen setup", "Family training"],
    who: "Patients with tracheostomy tubes requiring ongoing airway care.",
    description: "Expert tracheostomy care at home — sterile suction, cleaning, and tube changes by ICU-trained nurses.",
  },
  {
    slug: "ventilator-care",
    title: "Ventilator Care at Home",
    category: "critical",
    icon: "Zap",
    short: "Portable ventilator setup and 24×7 monitoring by ICU-trained nurses.",
    hero: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Home Ventilator Care in Delhi NCR | ICU Ventilator Setup — KinFlex",
    metaDescription: "Ventilator care at home in Delhi NCR — portable ICU ventilator setup with 24×7 monitoring by ICU-trained critical care nurses. Same-day setup.",
    intro: "For long-term ventilator-dependent patients, hospital stays become emotionally and financially exhausting. KinFlex sets up a portable ICU ventilator at home and provides a rotating team of critical care nurses to manage settings, humidification, suction and vitals. Care is coordinated with the treating pulmonologist or intensivist.",
    whoNeeds: [
      "Long-term ventilator-dependent patients",
      "Weaning support after prolonged ICU stay",
      "Neuro patients with respiratory failure",
      "COPD, ILD and advanced respiratory patients",
      "Palliative patients preferring home care",
    ],
    benefits: [
      "Home comfort while receiving ICU-level respiratory care",
      "Lower monthly cost vs hospital ICU",
      "Reduced hospital-acquired infection risk",
      "Family participation in care",
      "24×7 supervisor availability",
    ],
    situations: [
      "Setting up and calibrating ventilator settings",
      "Suction and humidification",
      "Tracheostomy care alongside ventilation",
      "Emergency response to alarms",
      "Weaning trials under doctor guidance",
    ],
    howWeHelp: "We install a portable ICU ventilator, configure it to the doctor's prescribed settings, and deploy critical care nurses in shifts. A supervisor and doctor stay on-call. Equipment servicing, backup power planning and oxygen coordination are all handled by KinFlex.",
    process: [
      { step: "01", title: "Case Review", desc: "Doctor's settings and case history reviewed." },
      { step: "02", title: "Equipment Setup", desc: "Portable ICU ventilator installed and tested." },
      { step: "03", title: "Nurse Rotation", desc: "ICU-trained nurses begin 24×7 shifts." },
      { step: "04", title: "Ongoing Support", desc: "Servicing, weaning trials, doctor coordination continue." },
    ],
    whyChoose: ["Portable ICU-grade ventilators", "ICU-experienced nurses", "24×7 supervisor", "Doctor coordination", "Same-day setup"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Can I rent a ventilator without a nurse?", a: "For clinical safety, KinFlex ventilator rentals include a nurse team. Trained monitoring is essential." },
      { q: "What if there's a power cut?", a: "Ventilators run on internal battery for 4–6 hours. We help plan UPS/backup power for longer outages." },
      { q: "Do you also arrange for the pulmonologist?", a: "We coordinate with your existing treating doctor and can arrange home visits by a consultant if needed." },
    ],
    related: ["critical-care-nurse", "icu-setup-at-home", "tracheostomy-care", "oxygen-therapy"],
    features: ["Portable ICU ventilator", "24×7 nurse rotation", "Suction & humidification", "Doctor coordination", "Same-day setup"],
    who: "Long-term ventilator-dependent patients preferring home care.",
    description: "Home ventilator care with portable ICU-grade equipment and ICU-trained critical care nurses.",
  },
  {
    slug: "oxygen-therapy",
    title: "Oxygen Therapy at Home",
    category: "critical",
    icon: "Wind",
    short: "Medical oxygen concentrators and cylinders with nurse-monitored oxygen therapy.",
    hero: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Oxygen Therapy at Home in Delhi NCR | Concentrators & Cylinders — KinFlex",
    metaDescription: "Home oxygen therapy in Delhi NCR — 5L/10L medical oxygen concentrators, portable cylinders, and nurse-supervised oxygen management.",
    intro: "Oxygen therapy at home helps patients with low blood oxygen breathe easier — whether the cause is COPD, post-COVID lung fibrosis, heart failure or sleep apnoea. KinFlex provides medical-grade oxygen concentrators and cylinders, and — where required — nurse-supervised oxygen management with regular SpO₂ monitoring.",
    whoNeeds: [
      "COPD and asthma patients with chronic hypoxia",
      "Post-COVID lung fibrosis or ILD patients",
      "Heart failure patients",
      "Palliative and end-stage patients",
      "Newborns and paediatric respiratory patients",
    ],
    benefits: [
      "Better sleep, less breathlessness",
      "Reduced hospital admissions",
      "Improved daily activity tolerance",
      "Continuous SpO₂ monitoring by nurse",
      "Rental flexibility — daily to monthly",
    ],
    situations: [
      "Continuous night oxygen for sleep apnoea",
      "Oxygen after exertion for COPD",
      "Post-hospital discharge oxygen support",
      "Palliative comfort care",
      "Emergency oxygen backup at home",
    ],
    howWeHelp: "We assess oxygen requirement, deliver a 5L or 10L medical-grade concentrator (or cylinders), and set up nasal cannula or mask correctly. Optionally, a KinFlex nurse monitors SpO₂ and flow rate on a schedule, and coordinates with the treating doctor for dose changes.",
    process: [
      { step: "01", title: "Assessment", desc: "SpO₂ check and oxygen prescription review." },
      { step: "02", title: "Delivery & Setup", desc: "Concentrator / cylinder delivered and installed." },
      { step: "03", title: "Family Training", desc: "Family briefed on flow settings & safety." },
      { step: "04", title: "Monitoring", desc: "Optional nurse-supervised monitoring & doctor coordination." },
    ],
    whyChoose: ["Medical-grade concentrators", "Nurse-supervised optional monitoring", "24×7 oxygen refill", "Same-day delivery", "Daily & monthly rental"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Do I need a doctor's prescription?", a: "Yes — for medical oxygen delivery, we require a prescription indicating oxygen flow rate and duration." },
      { q: "How long does the battery last on portable oxygen?", a: "Portable concentrators run 4–8 hours on battery depending on flow rate; larger units are mains-powered." },
      { q: "What if I need oxygen only at night?", a: "Night-only rentals are fully supported. We optimise for sleep-time flow rate and comfort." },
    ],
    related: ["oxygen-concentrator-rental", "ventilator-care", "bipap-rental", "cpap-rental"],
    features: ["5L / 10L concentrators", "Portable cylinders", "Nurse SpO₂ monitoring", "24×7 refills", "Doctor coordination"],
    who: "COPD, ILD, post-COVID, heart failure and palliative patients needing oxygen.",
    description: "Medical oxygen therapy at home with concentrators, cylinders and nurse-supervised monitoring.",
  },
  {
    slug: "ryles-tube-replacement",
    title: "Ryle's Tube Replacement",
    category: "procedure",
    icon: "PocketKnife",
    short: "Nasogastric tube insertion, replacement & feeding schedule management.",
    hero: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Ryle's Tube Insertion & Replacement at Home in Delhi NCR — KinFlex",
    metaDescription: "Safe Ryle's tube (NG) insertion, replacement and feeding schedule management at home by trained nurses across Delhi NCR.",
    intro: "For patients who can't swallow safely, a Ryle's tube (nasogastric tube) is essential for nutrition and medication. KinFlex nurses insert, replace and manage NG tubes at home — following strict sterile protocols. We also design feeding schedules, prevent aspiration risk, and train family caregivers on daily feeding.",
    whoNeeds: [
      "Stroke and neuro patients with dysphagia",
      "Post-oral / throat surgery patients",
      "Bedridden or unconscious patients",
      "Elderly patients unable to swallow safely",
      "Palliative care patients",
    ],
    benefits: [
      "Safe, aspiration-free feeding",
      "Structured medication delivery",
      "Reduced pneumonia risk",
      "Family trained on feeding basics",
      "Sterile insertion / change on schedule",
    ],
    situations: [
      "First-time NG tube insertion at home",
      "Scheduled tube replacement (every 3–6 weeks)",
      "Feed and medication schedule design",
      "Aspiration risk mitigation",
      "Tube blockage or displacement handling",
    ],
    howWeHelp: "A trained nurse inserts the NG tube with sterile technique, verifies placement, and then sets up a daily feeding & medication schedule with the family. We train the primary family caregiver on daily feeding, and schedule tube changes as needed.",
    process: [
      { step: "01", title: "Case Review", desc: "Doctor's advice, current tube type & schedule reviewed." },
      { step: "02", title: "Insertion / Change", desc: "NG tube inserted or replaced with sterile technique." },
      { step: "03", title: "Feed Schedule", desc: "Structured feeding & medication schedule set up." },
      { step: "04", title: "Family Training", desc: "Family trained on safe daily feeding & red flags." },
    ],
    whyChoose: ["Trained NG-tube nurses", "Sterile insertion technique", "Family training included", "24×7 support for issues", "Doctor coordination"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should an NG tube be changed?", a: "Typically every 3–6 weeks, or when it becomes clogged or displaced. Your doctor will confirm timing." },
      { q: "Can family members do the feeding themselves?", a: "Yes — we train family caregivers on safe daily feeding. Insertion, change and troubleshooting are done by the nurse." },
      { q: "What if the tube comes out at night?", a: "Call our 24×7 support line. Do not force it back in — a nurse will reach and safely re-insert." },
    ],
    related: ["home-nursing", "bedridden-patient-care", "stroke-rehabilitation", "palliative-care"],
    features: ["NG insertion & change", "Feeding schedule design", "Aspiration prevention", "Family training", "24×7 issue support"],
    who: "Dysphagia, stroke, post-surgery and long-term care patients.",
    description: "Safe Ryle's tube insertion, replacement and feeding schedule management by trained home-care nurses.",
  },
  {
    slug: "catheter-change",
    title: "Catheter Change at Home",
    category: "procedure",
    icon: "Droplet",
    short: "Sterile Foley catheter insertion, change & bladder management at home.",
    hero: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Foley Catheter Change at Home in Delhi NCR | Sterile Technique — KinFlex",
    metaDescription: "Safe Foley catheter insertion, replacement and daily bladder management at home in Delhi NCR by trained nurses. Male & female catheterisation.",
    intro: "Catheter care done improperly invites urinary infection and discomfort. KinFlex nurses handle Foley catheter insertion, scheduled change (every 4–6 weeks), and daily bladder care with strict sterile technique. This means fewer UTIs, better patient comfort, and structured infection-control protocols.",
    whoNeeds: [
      "Post-surgery patients needing bladder rest",
      "Bedridden patients unable to void",
      "Elderly patients with urinary retention",
      "Spinal cord and neuro patients",
      "Long-term palliative care patients",
    ],
    benefits: [
      "Reduced UTI risk",
      "Sterile scheduled change",
      "Better patient comfort",
      "Bladder wash if prescribed",
      "Family training on daily care",
    ],
    situations: [
      "Post-op urinary retention",
      "Prostate surgery follow-up",
      "Neurogenic bladder in spinal patients",
      "Chronic urinary retention in elderly",
      "Palliative comfort care",
    ],
    howWeHelp: "A trained nurse inserts or changes the Foley catheter with sterile technique, checks urine output and colour, and then sets up a daily care schedule. Bladder wash (if prescribed) is done safely. Family caregivers are trained on basic daily hygiene around the catheter.",
    process: [
      { step: "01", title: "Case Review", desc: "Doctor's prescription and current catheter type reviewed." },
      { step: "02", title: "Insertion / Change", desc: "Sterile catheter insertion or scheduled change." },
      { step: "03", title: "Daily Care Plan", desc: "Bladder hygiene routine + red-flag training for family." },
      { step: "04", title: "Follow-Up", desc: "Next scheduled change & UTI monitoring." },
    ],
    whyChoose: ["Male & female catheterisation", "Sterile technique", "UTI-prevention protocols", "Family training included", "24×7 issue support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should a Foley catheter be changed?", a: "Typically every 4–6 weeks, or sooner if blocked or if infection develops." },
      { q: "Is catheter change painful?", a: "There's mild discomfort but not pain. Trained technique minimises discomfort." },
      { q: "Can bladder wash be done at home?", a: "Yes — if prescribed by the doctor, bladder wash is done safely at home by a nurse." },
    ],
    related: ["urinary-catheter-care", "home-nursing", "bedridden-patient-care", "post-surgical-care"],
    features: ["Sterile insertion & change", "Bladder wash", "UTI prevention", "Family training", "24×7 issue support"],
    who: "Post-surgery, bedridden and long-term care patients requiring urinary catheters.",
    description: "Sterile Foley catheter change and bladder management at home by trained nurses.",
  },
  {
    slug: "urinary-catheter-care",
    title: "Urinary Catheter Care",
    category: "procedure",
    icon: "Droplet",
    short: "Ongoing daily care and hygiene for patients with urinary catheters.",
    hero: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Urinary Catheter Care at Home in Delhi NCR | Daily Hygiene — KinFlex",
    metaDescription: "Daily urinary catheter care at home in Delhi NCR — hygiene, bag change, UTI monitoring and family training by trained home care nurses.",
    intro: "Beyond insertion and change, urinary catheters need daily hygiene and monitoring to prevent UTIs and blockages. KinFlex offers ongoing urinary catheter care — daily cleaning, urine bag changes, output monitoring, and early UTI detection. Ideal for long-term catheter patients.",
    whoNeeds: [
      "Long-term catheter patients",
      "Bedridden patients",
      "Post-prostate surgery patients",
      "Neurogenic bladder patients",
      "Palliative and end-stage patients",
    ],
    benefits: [
      "Reduced UTI and blockage risk",
      "Structured daily hygiene",
      "Early detection of complications",
      "Better patient comfort",
      "Family peace of mind",
    ],
    situations: [
      "Daily meatal cleaning",
      "Urine bag change and drainage",
      "Output monitoring",
      "Early UTI detection",
      "Catheter blockage response",
    ],
    howWeHelp: "A KinFlex nurse or GDA visits daily (or as needed) to perform catheter hygiene, change the urine bag, monitor output and check for warning signs. Any signs of infection or blockage are escalated to the treating doctor.",
    process: [
      { step: "01", title: "Assessment", desc: "Current catheter care routine reviewed." },
      { step: "02", title: "Daily Care", desc: "Structured hygiene & monitoring begins." },
      { step: "03", title: "Family Training", desc: "Family trained on basics & red flags." },
      { step: "04", title: "Ongoing Support", desc: "Doctor coordination for infections & change schedule." },
    ],
    whyChoose: ["Structured daily hygiene", "Output monitoring & escalation", "Family training", "Doctor coordination", "24×7 support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should the urine bag be changed?", a: "The urine bag is typically emptied every 4–6 hours and changed every 5–7 days." },
      { q: "What are signs of a UTI?", a: "Fever, cloudy urine, foul smell, burning around the catheter site — call us immediately if these appear." },
      { q: "Can a GDA handle catheter care?", a: "Basic daily hygiene yes; anything clinical (insertion, wash, change) is done by a nurse." },
    ],
    related: ["catheter-change", "home-nursing", "bedridden-patient-care", "palliative-care"],
    features: ["Daily catheter hygiene", "Urine bag change", "Output monitoring", "UTI screening", "Doctor coordination"],
    who: "Long-term catheter patients needing daily hygiene and monitoring.",
    description: "Daily urinary catheter care at home — hygiene, monitoring and UTI prevention.",
  },
  {
    slug: "wound-dressing",
    title: "Wound Dressing at Home",
    category: "procedure",
    icon: "Bandage",
    short: "Sterile dressings for post-op wounds, pressure sores & diabetic ulcers.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Wound Dressing at Home in Delhi NCR | Post-Op & Diabetic Ulcer — KinFlex",
    metaDescription: "Sterile wound dressing at home in Delhi NCR — post-op wounds, pressure sores, diabetic foot ulcers. Advanced dressings by trained nurses.",
    intro: "Wound dressing done wrong slows healing and invites infection. KinFlex nurses use sterile technique and modern wound-care materials to accelerate healing for post-op wounds, pressure sores, diabetic ulcers and chronic wounds. Every wound is photographed and progress-tracked over time.",
    whoNeeds: [
      "Post-surgical patients (any specialty)",
      "Diabetic foot ulcer patients",
      "Bedridden patients with pressure sores",
      "Burn recovery patients",
      "Chronic non-healing wound patients",
    ],
    benefits: [
      "Faster wound healing",
      "Reduced infection risk",
      "Photo-tracked progress",
      "Advanced dressing options available",
      "Doctor coordination on wound changes",
    ],
    situations: [
      "Post-op suture-line dressing",
      "Diabetic foot ulcer management",
      "Pressure sore care in bedridden patients",
      "Burn dressing changes",
      "Chronic non-healing wound care",
    ],
    howWeHelp: "A nurse assesses the wound, chooses the right dressing type (simple, advanced, alginate, hydrocolloid, etc.), and follows a schedule of dressing changes. Photos are taken for progress tracking, and the treating doctor is looped in on any concerns.",
    process: [
      { step: "01", title: "Assessment", desc: "Wound reviewed with photos & doctor input." },
      { step: "02", title: "Dressing Plan", desc: "Right dressing type & change frequency chosen." },
      { step: "03", title: "Scheduled Care", desc: "Sterile dressing changes as per plan." },
      { step: "04", title: "Tracking", desc: "Photo-tracked healing & doctor coordination." },
    ],
    whyChoose: ["Sterile technique", "Advanced dressing materials", "Photo-tracked progress", "Doctor coordination", "Home comfort"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should a wound be dressed?", a: "Depends on wound type — simple wounds every 2–3 days, complex ones daily. Your doctor will guide frequency." },
      { q: "Do you handle diabetic foot ulcers?", a: "Yes — including offloading advice, moist wound-care and photo tracking. Coordination with the treating diabetologist is included." },
      { q: "What about advanced dressings like hydrocolloid?", a: "We stock and use advanced dressings when prescribed. Cost varies by dressing type." },
    ],
    related: ["home-nursing", "post-surgical-care", "bedridden-patient-care", "injection-at-home"],
    features: ["Sterile dressings", "Advanced dressing options", "Photo-tracked healing", "Doctor coordination", "Same-day booking"],
    who: "Post-op, diabetic ulcer, pressure sore and chronic wound patients.",
    description: "Sterile, structured wound dressing at home with photo-tracked healing.",
  },
  {
    slug: "injection-at-home",
    title: "Injection at Home",
    category: "procedure",
    icon: "Syringe",
    short: "Safe intramuscular, subcutaneous and IV injections at home.",
    hero: "https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Injection at Home in Delhi NCR | IM / SC / IV Injections — KinFlex",
    metaDescription: "Book same-day injections at home in Delhi NCR — IM, SC, IV injections, insulin, antibiotics, vaccinations by trained nurses.",
    intro: "Skip the hospital trip. KinFlex nurses come to your home for prescribed injections — insulin, antibiotics, painkillers, IV medications and vaccinations — all delivered safely, hygienically, and on schedule. Same-day bookings are available across Delhi NCR.",
    whoNeeds: [
      "Diabetic patients on insulin",
      "Patients on prescribed antibiotic courses",
      "Elderly patients unable to travel to hospitals",
      "Post-op patients needing scheduled injections",
      "Patients preferring home vaccinations",
    ],
    benefits: [
      "Skip hospital queues & travel",
      "Same-day availability",
      "Sterile, safe technique",
      "Family peace of mind",
      "Doctor coordination available",
    ],
    situations: [
      "Daily / weekly insulin injections",
      "IV antibiotic course at home",
      "Vitamin B12, iron, or vaccine injections",
      "Post-op painkiller injections",
      "Fertility hormone injections",
    ],
    howWeHelp: "You share the doctor's prescription and preferred timing. A nurse arrives on schedule, verifies medication and dose, gives the injection with sterile technique, and documents the visit. For long courses, we set up a recurring schedule.",
    process: [
      { step: "01", title: "Share Prescription", desc: "Send us the doctor's prescription via WhatsApp or form." },
      { step: "02", title: "Schedule Visit", desc: "Choose a preferred time — same-day if urgent." },
      { step: "03", title: "Nurse Visits", desc: "Nurse arrives, verifies dose & administers safely." },
      { step: "04", title: "Documentation", desc: "Visit logged & follow-up scheduled if needed." },
    ],
    whyChoose: ["Same-day bookings", "Sterile disposable syringes", "Trained nurses only", "Full documentation", "Home comfort"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Do I need to provide the medication?", a: "Usually yes — you provide the prescribed medication; we bring sterile syringes and administer safely. In some cases we can arrange medication too." },
      { q: "How much does a home injection cost?", a: "Cost varies by injection type and location. Contact us for a transparent quote." },
      { q: "Can you give IV antibiotics at home?", a: "Yes — including full IV antibiotic courses under doctor supervision." },
    ],
    related: ["iv-infusion-at-home", "home-nursing", "doctor-visit-at-home", "wound-dressing"],
    features: ["IM / SC / IV injections", "Insulin & antibiotics", "Vaccinations", "Sterile technique", "Same-day booking"],
    who: "Patients needing prescribed injections at home.",
    description: "Safe, same-day injections at home — IM, SC, IV by trained nurses.",
  },
  {
    slug: "iv-infusion-at-home",
    title: "IV Infusion at Home",
    category: "procedure",
    icon: "Droplets",
    short: "IV fluid, antibiotics and drug infusion at home under nurse supervision.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "IV Infusion at Home in Delhi NCR | IV Fluids & Antibiotics — KinFlex",
    metaDescription: "IV infusion at home in Delhi NCR — cannulation, IV fluids, IV antibiotics, chemo cycles and drug infusion under nurse supervision.",
    intro: "IV therapies delivered safely at home under trained nurse supervision — from routine fluids to complex antibiotic courses and chemotherapy cycles. Every drip is set up with sterile technique, vitals are monitored throughout, and the treating doctor's protocol is followed to the letter.",
    whoNeeds: [
      "Dehydration and viral illness patients",
      "Post-op recovery patients needing IV antibiotics",
      "Chemotherapy cycle patients",
      "Chronic illness patients on IV medications",
      "Elderly patients preferring home IV over hospital day-care",
    ],
    benefits: [
      "No hospital day-care needed",
      "Comfort and privacy of home",
      "Continuous vitals monitoring during infusion",
      "Faster turnaround for repeated cycles",
      "Family involvement",
    ],
    situations: [
      "Daily IV antibiotic dose",
      "Fluid & electrolyte replacement",
      "IV iron and vitamin infusions",
      "Chemotherapy cycles per oncologist protocol",
      "Hyperemesis (pregnancy) IV support",
    ],
    howWeHelp: "A nurse arrives with sterile IV supplies, sets up cannulation, and starts the prescribed infusion. Vitals are monitored throughout. The cannula is removed or maintained per doctor's advice. Every session is documented.",
    process: [
      { step: "01", title: "Share Prescription", desc: "Doctor's IV protocol shared with our team." },
      { step: "02", title: "Nurse Visit", desc: "Nurse visits with sterile IV supplies." },
      { step: "03", title: "Infusion", desc: "IV administered with vitals monitoring throughout." },
      { step: "04", title: "Documentation", desc: "Session logged; next cycle scheduled." },
    ],
    whyChoose: ["Nurse-supervised IV", "Sterile disposable supplies", "Vitals monitoring during infusion", "Doctor coordination", "Same-day bookings"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Can you set up an IV cannula at home?", a: "Yes — our nurses are trained on adult and paediatric cannulation." },
      { q: "Do you handle chemotherapy at home?", a: "Yes, in coordination with the treating oncologist and per protocol. Safety is our top priority." },
      { q: "How long does an IV infusion take?", a: "Depends on the drug — anywhere from 30 minutes to 4 hours. Our nurse stays throughout." },
    ],
    related: ["injection-at-home", "home-nursing", "critical-care-nurse", "doctor-visit-at-home"],
    features: ["IV cannulation & drip", "Antibiotic & chemo cycles", "Fluid & electrolyte therapy", "Vitals monitoring", "Doctor coordination"],
    who: "Dehydration, chemo, chronic infection, or fluid-therapy patients.",
    description: "Safe IV infusion at home — under trained nurse supervision, following doctor's protocol.",
  },
  {
    slug: "doctor-visit-at-home",
    title: "Doctor Visit at Home",
    category: "consult",
    icon: "UserPlus",
    short: "General physician and specialist doctor consultations at your home.",
    hero: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Doctor Visit at Home in Delhi NCR | GP & Specialist — KinFlex",
    metaDescription: "General physicians and specialist doctors visit at home in Delhi NCR — consultations, prescriptions, lab coordination, follow-ups. Same-day slots.",
    intro: "For patients unable to travel — elderly, bedridden, post-op — a home doctor visit is often better than an OPD trip. KinFlex arranges general physicians and specialist consultations at home, including prescriptions, lab test coordination and follow-ups.",
    whoNeeds: [
      "Elderly patients with mobility issues",
      "Bedridden or post-surgery patients",
      "Chronic illness follow-ups",
      "Patients preferring privacy over hospital OPD",
      "Home ICU patients needing specialist review",
    ],
    benefits: [
      "Skip OPD queues and travel",
      "Focused, unhurried consultation at home",
      "Family present during discussion",
      "Lab tests coordinated at home",
      "Same-day availability in most areas",
    ],
    situations: [
      "General physician for fever / illness",
      "Diabetologist for chronic diabetes follow-up",
      "Cardiologist review post heart-attack",
      "Palliative care consultation",
      "Home ICU patient specialist review",
    ],
    howWeHelp: "Share the case briefly. We assign the appropriate general physician or specialist. The doctor visits at a scheduled time, examines the patient, prescribes treatment, and coordinates any tests or follow-up.",
    process: [
      { step: "01", title: "Case Brief", desc: "Share patient details & required specialty." },
      { step: "02", title: "Doctor Assigned", desc: "GP or specialist matched & scheduled." },
      { step: "03", title: "Home Consultation", desc: "Doctor visits, examines & prescribes." },
      { step: "04", title: "Follow-Up", desc: "Prescriptions, lab tests & next visit coordinated." },
    ],
    whyChoose: ["GPs & specialists on panel", "Same-day scheduling", "Lab test coordination", "Digital prescription", "Follow-up support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "What specialists visit at home?", a: "Primarily general physicians, geriatricians, and palliative care doctors. Cardiologists, pulmonologists, and others available on request." },
      { q: "How much does a home doctor visit cost?", a: "Depends on specialty and area. Contact us for a transparent quote." },
      { q: "Can the doctor prescribe medicines?", a: "Yes — a digital prescription is provided at the end of the visit." },
    ],
    related: ["home-nursing", "elder-care", "physiotherapy-at-home", "iv-infusion-at-home"],
    features: ["GP & specialist visits", "Prescription at home", "Lab test coordination", "Follow-up scheduling", "Same-day slots"],
    who: "Elderly, non-ambulatory, chronic illness and post-op follow-up patients.",
    description: "General physicians and specialists visit at home — with prescriptions and lab coordination.",
  },
  {
    slug: "bedridden-patient-care",
    title: "Bedridden Patient Care",
    category: "care",
    icon: "BedDouble",
    short: "Specialised care for bedridden patients — positioning, hygiene, feeding.",
    hero: "https://images.pexels.com/photos/5452268/pexels-photo-5452268.jpeg?auto=compress&cs=tinysrgb&h=650",
    metaTitle: "Bedridden Patient Care at Home in Delhi NCR — KinFlex",
    metaDescription: "Specialised bedridden patient care at home in Delhi NCR — 2-hourly positioning, hygiene, pressure sore prevention, feeding & bowel care.",
    intro: "Caring for a bedridden patient is a full-time discipline. KinFlex nurses and GDAs handle 2-hourly positioning to prevent pressure sores, sponge baths, feeding, bowel and bladder care, and gentle range-of-motion exercises. We focus on comfort, hygiene, and dignity.",
    whoNeeds: [
      "Post-stroke and paralysis patients",
      "Coma and semi-conscious patients",
      "Advanced neurological patients",
      "Palliative and end-stage patients",
      "Long-term post-surgery patients",
    ],
    benefits: [
      "Pressure sore prevention through positioning",
      "Dignified hygiene routines",
      "Structured feeding schedules",
      "Range-of-motion exercises",
      "Family involvement and updates",
    ],
    situations: [
      "2-hourly positioning to prevent bedsores",
      "Sponge bath & personal hygiene",
      "Ryle's tube or oral feeding",
      "Bowel / bladder management",
      "Passive range-of-motion exercises",
    ],
    howWeHelp: "A KinFlex team (nurse + GDA where needed) sets up a structured daily plan — position rotation, hygiene schedule, feeding routine, physiotherapy. A clinical supervisor stays engaged to prevent complications and coordinate with the treating doctor.",
    process: [
      { step: "01", title: "Assessment", desc: "Patient condition & family capacity reviewed." },
      { step: "02", title: "Care Plan", desc: "Structured daily plan designed." },
      { step: "03", title: "Team Deployed", desc: "Nurse and/or GDA start scheduled care." },
      { step: "04", title: "Supervision", desc: "Clinical supervisor & doctor coordination continue." },
    ],
    whyChoose: ["Pressure-sore prevention protocols", "Structured hygiene & feeding", "Passive physiotherapy included", "Doctor coordination", "24×7 shifts"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How do you prevent bedsores?", a: "Through 2-hourly positioning, air mattress use, skin care and regular assessment." },
      { q: "Do you provide the air mattress?", a: "Yes — anti-decubitus air mattresses are available on rent alongside care." },
      { q: "Can we combine bedridden care with physiotherapy?", a: "Absolutely — passive range-of-motion is part of daily care, with structured physio sessions scheduled separately." },
    ],
    related: ["home-nursing", "patient-care-attendant", "palliative-care", "physiotherapy-at-home"],
    features: ["2-hourly positioning", "Sponge bath & hygiene", "Feeding support", "Bowel & bladder care", "Passive ROM exercises"],
    who: "Long-term bedridden patients — paralysis, coma, neuro, palliative.",
    description: "Bedridden patient care at home — positioning, hygiene, feeding and dignity.",
  },
  {
    slug: "palliative-care",
    title: "Palliative Care at Home",
    category: "care",
    icon: "Heart",
    short: "Compassionate palliative care focused on comfort, dignity and family support.",
    hero: "https://images.unsplash.com/photo-1758691462477-976f771224d8?crop=entropy&cs=srgb&fm=jpg&q=85",
    metaTitle: "Palliative Care at Home in Delhi NCR | Comfort & Dignity — KinFlex",
    metaDescription: "Palliative care at home in Delhi NCR — pain management, symptom control, emotional support and family counselling. Compassionate, dignified care.",
    intro: "Palliative care is about comfort, dignity and support — for the patient and the family. KinFlex offers structured palliative care at home for advanced illness, focusing on pain control, symptom management, emotional support and family counselling. We work with palliative care doctors and honour the family's care goals.",
    whoNeeds: [
      "Advanced cancer patients",
      "End-stage organ failure patients",
      "Advanced neurological illness patients",
      "Elderly patients with multiple chronic conditions",
      "Families needing structured end-of-life support",
    ],
    benefits: [
      "Focused pain and symptom relief",
      "Home comfort in the final phase of life",
      "Emotional support for patient and family",
      "Structured medication management",
      "Doctor and hospice coordination",
    ],
    situations: [
      "Pain management for advanced cancer",
      "Symptom control (breathlessness, nausea, agitation)",
      "Bowel and bladder care",
      "Feeding and hydration decisions",
      "Family and grief counselling",
    ],
    howWeHelp: "Palliative nurses design a comfort-focused care plan with the family and treating doctor. Priorities are pain and symptom relief, dignity, emotional support, and honouring the family's wishes about goals of care. Bereavement support is available where needed.",
    process: [
      { step: "01", title: "Family Conversation", desc: "Understand goals of care & family wishes." },
      { step: "02", title: "Care Plan", desc: "Pain & symptom management plan designed." },
      { step: "03", title: "Ongoing Care", desc: "Nurses provide comfort care with doctor coordination." },
      { step: "04", title: "Support", desc: "Emotional support & bereavement care where needed." },
    ],
    whyChoose: ["Palliative-trained nurses", "Doctor coordination", "Emotional support included", "Home comfort focus", "24×7 availability"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Is palliative care only for the last few days?", a: "No — palliative care can start alongside curative treatment. It's about comfort and dignity throughout advanced illness." },
      { q: "Do you handle pain management?", a: "Yes — including oral, IM, IV and subcutaneous pain medication under palliative doctor supervision." },
      { q: "Do you provide bereavement support?", a: "Basic bereavement support is included; extended counselling is available on request." },
    ],
    related: ["home-nursing", "critical-care-nurse", "bedridden-patient-care", "doctor-visit-at-home"],
    features: ["Pain & symptom management", "Emotional support", "Doctor coordination", "Family counselling", "24×7 availability"],
    who: "Advanced illness and end-of-life patients seeking comfort and dignity at home.",
    description: "Compassionate palliative care at home — comfort, dignity, symptom control and family support.",
  },
  {
    slug: "post-surgical-care",
    title: "Post Surgical Care at Home",
    category: "recovery",
    icon: "Bandage",
    short: "Structured recovery care after surgery — dressings, monitoring, physio.",
    hero: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Post Surgical Care at Home in Delhi NCR | Recovery Program — KinFlex",
    metaDescription: "Structured post surgical recovery at home in Delhi NCR — dressings, vitals monitoring, physio & family training. Faster, safer recovery.",
    intro: "The 2–4 weeks after major surgery are critical. KinFlex's post-surgical care program combines nursing (dressings, vitals, medication), physiotherapy (mobility, breathing exercises) and family training. Result: fewer complications, faster recovery, and lower risk of re-admission.",
    whoNeeds: [
      "Knee / hip replacement patients",
      "Cardiac surgery (CABG, valve) patients",
      "Abdominal surgery patients",
      "Spine surgery patients",
      "Any elective or emergency major surgery patient",
    ],
    benefits: [
      "Faster, structured recovery",
      "Reduced complication risk",
      "One care team through recovery",
      "Family training on daily routines",
      "Doctor coordination on follow-ups",
    ],
    situations: [
      "Post-op wound dressing schedule",
      "Vitals monitoring at home",
      "Physiotherapy for mobility recovery",
      "Injection and IV antibiotic courses",
      "Bowel and bladder support post-surgery",
    ],
    howWeHelp: "We build a personalised recovery plan around your discharge summary — nursing visits, physiotherapy sessions, medication schedule and dietary guidance. Progress is tracked, and the treating surgeon is looped in as needed.",
    process: [
      { step: "01", title: "Discharge Review", desc: "Discharge summary reviewed by our clinical team." },
      { step: "02", title: "Recovery Plan", desc: "Nursing + physio + medication plan designed." },
      { step: "03", title: "Home Care Begins", desc: "Scheduled visits with progress tracking." },
      { step: "04", title: "Follow-Up", desc: "Coordination with surgeon on follow-up review." },
    ],
    whyChoose: ["Complete post-op program", "Nursing + physio together", "Progress tracking", "Doctor coordination", "Family training"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How long is a typical post-op program?", a: "Usually 2–6 weeks depending on the surgery." },
      { q: "Do I need physiotherapy after every surgery?", a: "For orthopaedic, spine, and cardiac surgeries — yes. Others depend on the surgeon's advice." },
      { q: "Can you handle IV antibiotic courses at home?", a: "Yes — IV antibiotics, dressings, monitoring — all included in the post-op program." },
    ],
    related: ["home-nursing", "physiotherapy-at-home", "wound-dressing", "iv-infusion-at-home"],
    features: ["Dressing & wound care", "Vitals monitoring", "Physio & mobility", "Medication schedules", "Family training"],
    who: "Any post-major-surgery patient recovering at home.",
    description: "Structured post-surgical recovery at home — nursing, physio and family training combined.",
  },
  {
    slug: "stroke-rehabilitation",
    title: "Stroke Rehabilitation at Home",
    category: "rehab",
    icon: "Move",
    short: "Structured neuro-rehab combining physio, speech and occupational therapy.",
    hero: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Stroke Rehabilitation at Home in Delhi NCR | Neuro-Rehab — KinFlex",
    metaDescription: "Home stroke rehabilitation in Delhi NCR — physio, speech and occupational therapy for stroke survivors. Measurable, structured recovery.",
    intro: "Stroke recovery is a marathon — and the first 6 months are gold. KinFlex's stroke rehab program brings physiotherapy, speech therapy (where needed) and occupational therapy home. Structured, measured, and paced to the patient's ability. Families become part of the rehab journey.",
    whoNeeds: [
      "Recent stroke survivors (first 6 months)",
      "Older stroke patients with residual disability",
      "Post-TIA patients needing prevention support",
      "Stroke patients with speech difficulties",
      "Stroke patients with paralysis or weakness",
    ],
    benefits: [
      "Faster neuro-recovery",
      "Improved mobility and strength",
      "Better speech and communication",
      "Home-friendly therapy plans",
      "Emotional and family support",
    ],
    situations: [
      "Post-stroke mobility rehab",
      "Speech therapy for aphasia",
      "Swallowing rehab for dysphagia",
      "Upper-limb functional recovery",
      "Fall-prevention and balance training",
    ],
    howWeHelp: "A neuro-physiotherapist leads the rehab plan with the family. Sessions include mobility work, strengthening, functional exercises and — if needed — speech and swallowing therapy. Progress is tracked and re-assessed every 2 weeks.",
    process: [
      { step: "01", title: "Assessment", desc: "Neuro-physio evaluates deficits & sets goals." },
      { step: "02", title: "Rehab Plan", desc: "Physio + speech + OT plan designed." },
      { step: "03", title: "Home Sessions", desc: "Structured therapy sessions with family involvement." },
      { step: "04", title: "Re-Assessment", desc: "Progress reviewed every 2 weeks & plan adjusted." },
    ],
    whyChoose: ["Neuro-physiotherapy specialists", "Speech and OT support available", "Measurable goals", "Family participation", "Doctor coordination"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How soon should stroke rehab start?", a: "Ideally within 2 weeks of the stroke, once medically stable. Earlier is better." },
      { q: "How many sessions per week?", a: "Typically 4–6 sessions per week in the first 3 months, then tapered." },
      { q: "Do you provide speech therapy at home?", a: "Yes — speech and swallowing therapy is available on request as part of the rehab plan." },
    ],
    related: ["physiotherapy-at-home", "paralysis-rehabilitation", "home-nursing", "elder-care"],
    features: ["Neuro-physio", "Speech & swallowing therapy", "Occupational therapy", "Family involvement", "Progress tracking"],
    who: "Stroke survivors in acute and chronic recovery phases.",
    description: "Structured stroke rehab at home — physio, speech and OT combined for measurable neuro-recovery.",
  },
  {
    slug: "paralysis-rehabilitation",
    title: "Paralysis Rehabilitation at Home",
    category: "rehab",
    icon: "Move",
    short: "Comprehensive paralysis rehab with physio, nursing and family support.",
    hero: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Paralysis Rehabilitation at Home in Delhi NCR — KinFlex",
    metaDescription: "Home paralysis rehab in Delhi NCR — combined physio, nursing and family training for stroke, spinal cord and neuro paralysis patients.",
    intro: "Paralysis rehab requires patience, structure, and expertise. KinFlex combines neuro-physiotherapy, nursing (for hygiene, catheter, positioning) and family training into one integrated program. Focus areas: preventing complications, regaining function, and improving quality of life.",
    whoNeeds: [
      "Stroke-related paralysis patients",
      "Spinal cord injury patients",
      "Guillain-Barré and neuro-inflammatory patients",
      "Post-brain surgery patients with paralysis",
      "Chronic paralysis patients needing support",
    ],
    benefits: [
      "Structured functional recovery",
      "Complication prevention (sores, contractures)",
      "Home-friendly rehab plan",
      "Family involvement",
      "Doctor coordination",
    ],
    situations: [
      "Passive and active range-of-motion work",
      "Strengthening and functional training",
      "Bowel / bladder training",
      "Assistive device advice (walker, wheelchair)",
      "Fall-prevention and safety",
    ],
    howWeHelp: "Physiotherapist and nurse work as a team. Physio drives functional recovery. Nurse handles hygiene, catheter care and prevention of complications. Family is trained on daily routines. Progress is tracked and reviewed regularly.",
    process: [
      { step: "01", title: "Assessment", desc: "Comprehensive neuro & functional assessment." },
      { step: "02", title: "Integrated Plan", desc: "Physio + nursing + family training plan." },
      { step: "03", title: "Home Program", desc: "Structured daily therapy & care." },
      { step: "04", title: "Reviews", desc: "Progress tracked every 2 weeks." },
    ],
    whyChoose: ["Physio + nursing integrated", "Complication prevention focus", "Family training", "Assistive device advice", "Doctor coordination"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How long does paralysis rehab take?", a: "Recovery varies. Acute rehab is usually 3–6 months intensive, then longer maintenance." },
      { q: "Do you help with wheelchair / assistive devices?", a: "Yes — we advise and, where needed, arrange assistive devices on rent." },
      { q: "Can you handle spinal cord injury rehab?", a: "Yes — with nursing, physio and family training tailored to level of injury." },
    ],
    related: ["stroke-rehabilitation", "physiotherapy-at-home", "home-nursing", "bedridden-patient-care"],
    features: ["Physio + nursing team", "Range-of-motion & strengthening", "Bowel & bladder training", "Assistive device advice", "Family training"],
    who: "Stroke, spinal cord and neuro paralysis patients in home recovery.",
    description: "Integrated paralysis rehab at home — physio, nursing and family support combined.",
  },
  {
    slug: "medical-equipment-rental",
    title: "Medical Equipment Rental",
    category: "equipment",
    icon: "Boxes",
    short: "Rent ICU beds, ventilators, oxygen, wheelchairs & more.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Medical Equipment on Rent in Delhi NCR | ICU Bed, Ventilator, Oxygen — KinFlex",
    metaDescription: "Rent ICU beds, ventilators, oxygen concentrators, BiPAP, CPAP, wheelchairs and more across Delhi NCR. Delivery, installation, pickup included.",
    intro: "Setting up home care shouldn't need a big equipment purchase. KinFlex rents ICU-grade medical equipment across Delhi NCR — ICU beds, ventilators, oxygen concentrators, BiPAP/CPAP machines, suction machines, wheelchairs, walkers, and monitors. All prices include delivery, installation, training and pickup.",
    whoNeeds: [
      "Families setting up home care",
      "Post-op patients needing temporary equipment",
      "Elderly patients needing mobility aids",
      "Long-term chronic illness families",
      "Palliative care setups",
    ],
    benefits: [
      "No large capital purchase",
      "Delivery, installation, pickup included",
      "Flexible daily, weekly & monthly plans",
      "Sanitised, quality-checked equipment",
      "24×7 support hotline",
    ],
    situations: [
      "Setting up home care after hospital discharge",
      "Temporary mobility needs post-op",
      "Emergency oxygen backup",
      "Home ICU setup",
      "Palliative care equipment",
    ],
    howWeHelp: "Tell us what you need — we deliver, install and train the family. Any issues, we replace or service quickly. Plans are flexible — daily, weekly, monthly — and pickup is on request.",
    process: [
      { step: "01", title: "Requirement", desc: "Share what equipment you need." },
      { step: "02", title: "Delivery", desc: "Delivered & installed at home." },
      { step: "03", title: "Training", desc: "Family trained on operation & safety." },
      { step: "04", title: "Support & Pickup", desc: "24×7 support; free pickup when done." },
    ],
    whyChoose: ["ICU-grade quality", "Same-day delivery", "Delivery & pickup included", "24×7 support", "Flexible plans"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Do you deliver equipment same-day?", a: "Yes — most equipment can be delivered same-day across Delhi NCR." },
      { q: "Is delivery and installation charged separately?", a: "No — delivery, installation and pickup are included in the rental." },
      { q: "What if the equipment breaks down?", a: "Call us any time — we replace / service quickly, usually within a few hours." },
    ],
    related: ["hospital-bed-rental", "oxygen-concentrator-rental", "wheelchair-rental", "icu-setup-at-home"],
    features: ["ICU beds & ventilators", "Oxygen concentrators & BiPAP", "Wheelchairs & walkers", "Delivery + pickup", "24×7 support"],
    who: "Families setting up home care needing medical equipment.",
    description: "Rent ICU-grade equipment across Delhi NCR — with delivery, installation, training and pickup included.",
  },
  {
    slug: "hospital-bed-rental",
    title: "Hospital Bed Rental",
    category: "equipment",
    icon: "BedDouble",
    short: "Semi-fowler and fully motorised ICU beds on rent for home care.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Hospital Bed on Rent in Delhi NCR | Semi-Fowler & ICU Bed — KinFlex",
    metaDescription: "Rent hospital beds in Delhi NCR — semi-fowler and 5-function motorised ICU beds with mattress. Same-day delivery, flexible plans.",
    intro: "A proper hospital bed transforms home care — allowing safe positioning, easier transfers and better pressure management. KinFlex rents semi-fowler manual beds and 5-function fully motorised ICU beds — with high-quality mattresses and side rails — across Delhi NCR.",
    whoNeeds: [
      "Bedridden patients",
      "Post-major-surgery recovery patients",
      "Elderly patients needing safe positioning",
      "ICU discharge patients continuing home care",
      "Palliative care patients",
    ],
    benefits: [
      "Safer positioning and transfers",
      "Better pressure management",
      "Adjustable head and knee angles",
      "Side rails prevent falls",
      "Comfortable for long-term use",
    ],
    situations: [
      "Long-term bedridden care",
      "Post-op recovery",
      "Home ICU setup",
      "Elderly patients preferring bed comfort",
      "Palliative care bedside setup",
    ],
    howWeHelp: "Choose semi-fowler (manual crank) or fully motorised (electric). We deliver and assemble at your home, brief family on operation, and pick up when done. Mattress and side rails are included.",
    process: [
      { step: "01", title: "Choose Bed", desc: "Semi-fowler or motorised ICU bed." },
      { step: "02", title: "Delivery", desc: "Same-day delivery & assembly at home." },
      { step: "03", title: "Family Training", desc: "Operation & safety briefing." },
      { step: "04", title: "Pickup", desc: "Free pickup when rental ends." },
    ],
    whyChoose: ["Semi-fowler & motorised options", "Mattress + side rails included", "Same-day delivery", "Free pickup", "Sanitised units"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "What's the difference between semi-fowler and ICU bed?", a: "Semi-fowler has manual crank for head/knee adjustment. ICU bed is electric with 5-function motor, side rails, and often trolley wheels." },
      { q: "Is the mattress included?", a: "Yes — standard mattress is included; air mattresses are available at additional rental." },
      { q: "Is same-day delivery available?", a: "Yes — across Delhi NCR, most bed rentals are delivered same-day." },
    ],
    related: ["medical-equipment-rental", "icu-setup-at-home", "bedridden-patient-care", "patient-monitor-rental"],
    features: ["Semi-fowler / ICU bed", "Mattress + side rails", "Same-day delivery", "Free pickup", "Motorised options"],
    who: "Bedridden, post-op, elderly and long-term home care patients.",
    description: "Semi-fowler and motorised ICU beds on rent — with mattress, side rails and same-day delivery.",
  },
  {
    slug: "oxygen-concentrator-rental",
    title: "Oxygen Concentrator Rental",
    category: "equipment",
    icon: "Wind",
    short: "5L / 10L medical oxygen concentrators on rent with same-day delivery.",
    hero: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Oxygen Concentrator on Rent in Delhi NCR | 5L / 10L — KinFlex",
    metaDescription: "Rent 5L and 10L medical oxygen concentrators in Delhi NCR — same-day delivery, sanitised units, 24×7 support. Flexible daily to monthly plans.",
    intro: "Oxygen concentrators pull oxygen directly from room air — no cylinders, no refills. KinFlex rents 5L and 10L medical-grade concentrators across Delhi NCR, with same-day delivery, family training, and 24×7 breakdown support.",
    whoNeeds: [
      "COPD, ILD and post-COVID lung patients",
      "Heart failure patients on chronic oxygen",
      "Palliative care patients",
      "Post-op patients needing temporary oxygen",
      "Anyone with prescribed home oxygen",
    ],
    benefits: [
      "Unlimited oxygen — no refills",
      "Quiet, home-friendly operation",
      "Same-day delivery",
      "24×7 breakdown support",
      "Flexible rental plans",
    ],
    situations: [
      "Continuous chronic oxygen use",
      "Night-only oxygen for sleep apnoea",
      "Post-hospital discharge oxygen",
      "Emergency oxygen backup",
      "Palliative comfort care",
    ],
    howWeHelp: "Share your prescription. We deliver the right concentrator (5L or 10L) same-day, install, train the family, and stay on-call for any issues. Backup portable cylinders can be arranged for outings.",
    process: [
      { step: "01", title: "Prescription", desc: "Doctor's prescription confirms flow rate needed." },
      { step: "02", title: "Delivery", desc: "5L or 10L concentrator delivered same-day." },
      { step: "03", title: "Training", desc: "Family trained on operation & maintenance." },
      { step: "04", title: "Support", desc: "24×7 helpline for any issues." },
    ],
    whyChoose: ["Medical-grade 5L / 10L units", "Same-day delivery", "Sanitised units", "24×7 breakdown support", "Backup cylinders on request"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "5L vs 10L — which one do I need?", a: "5L is for continuous low-flow (1–5L/min). 10L is for higher flows or when running two patients or high-flow needs." },
      { q: "Does it need a special power outlet?", a: "No — standard 220V plug. Power consumption is similar to a small fridge." },
      { q: "What about power cuts?", a: "We recommend a UPS or backup portable cylinder for uninterrupted therapy." },
    ],
    related: ["oxygen-therapy", "medical-equipment-rental", "bipap-rental", "cpap-rental"],
    features: ["5L / 10L concentrators", "Same-day delivery", "24×7 support", "Backup cylinders", "Flexible plans"],
    who: "Home oxygen therapy patients — chronic and post-op.",
    description: "Medical oxygen concentrators (5L & 10L) on rent across Delhi NCR with same-day delivery.",
  },
  {
    slug: "bipap-rental",
    title: "BiPAP Rental",
    category: "equipment",
    icon: "Activity",
    short: "BiPAP machines on rent for respiratory failure and severe sleep apnoea.",
    hero: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "BiPAP on Rent in Delhi NCR | Non-Invasive Ventilation — KinFlex",
    metaDescription: "Rent BiPAP machines in Delhi NCR — non-invasive ventilation for COPD, respiratory failure and severe sleep apnoea. Same-day delivery.",
    intro: "BiPAP (Bi-level Positive Airway Pressure) supports breathing without invasive ventilation — ideal for COPD exacerbations, type 2 respiratory failure, and severe sleep apnoea. KinFlex rents high-quality BiPAP machines with masks and humidifiers.",
    whoNeeds: [
      "COPD patients with type 2 respiratory failure",
      "Severe sleep apnoea patients needing pressure support",
      "Post-hospital discharge respiratory patients",
      "ALS / neuro respiratory failure patients",
      "Overlap syndrome (COPD + sleep apnoea) patients",
    ],
    benefits: [
      "Non-invasive respiratory support",
      "Reduces need for intubation",
      "Better sleep and daytime energy",
      "Home comfort",
      "Flexible rental",
    ],
    situations: [
      "Overnight BiPAP for COPD stability",
      "Severe sleep apnoea (CPAP not enough)",
      "Weaning after prolonged ventilation",
      "Neuro-muscular respiratory weakness",
    ],
    howWeHelp: "Doctor's prescription confirms IPAP / EPAP settings. We deliver a BiPAP unit, appropriate mask (nasal, oro-nasal), and humidifier. Family is trained on setup, cleaning and troubleshooting.",
    process: [
      { step: "01", title: "Prescription", desc: "Doctor's BiPAP settings confirmed." },
      { step: "02", title: "Delivery", desc: "BiPAP with mask & humidifier delivered." },
      { step: "03", title: "Setup & Training", desc: "Family briefed on settings & cleaning." },
      { step: "04", title: "Support", desc: "24×7 issue support & mask replacements." },
    ],
    whyChoose: ["Medical-grade BiPAP units", "Mask fitting included", "Humidifier included", "Same-day delivery", "24×7 support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "BiPAP vs CPAP — what's the difference?", a: "CPAP delivers one continuous pressure. BiPAP delivers two levels — higher for breathing in, lower for breathing out — better for respiratory failure and severe apnoea." },
      { q: "Do I need a specific mask?", a: "Yes — we help fit the right mask (nasal, nasal-pillow or oro-nasal) based on the patient's face and breathing pattern." },
      { q: "Is the humidifier necessary?", a: "Strongly recommended — it prevents dryness of throat and nose, especially for nightly use." },
    ],
    related: ["cpap-rental", "oxygen-concentrator-rental", "ventilator-care", "oxygen-therapy"],
    features: ["Medical BiPAP", "Mask + humidifier", "Same-day delivery", "24×7 support", "Family training"],
    who: "COPD, respiratory failure and severe sleep apnoea patients.",
    description: "Non-invasive BiPAP therapy on rent across Delhi NCR — for COPD, respiratory failure and sleep apnoea.",
  },
  {
    slug: "cpap-rental",
    title: "CPAP Rental",
    category: "equipment",
    icon: "Activity",
    short: "CPAP machines on rent for sleep apnoea treatment.",
    hero: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "CPAP on Rent in Delhi NCR | Sleep Apnoea Therapy — KinFlex",
    metaDescription: "Rent CPAP machines in Delhi NCR for sleep apnoea therapy — mask, humidifier and 24×7 support included. Same-day delivery.",
    intro: "CPAP delivers a steady stream of air through a mask — keeping the airway open during sleep. It's the gold-standard treatment for obstructive sleep apnoea (OSA). KinFlex rents CPAP machines with mask fitting, humidifier and 24×7 support across Delhi NCR.",
    whoNeeds: [
      "Diagnosed obstructive sleep apnoea patients",
      "Loud snoring + daytime sleepiness patients",
      "Patients wanting to trial CPAP before purchase",
      "Post-diagnosis sleep-study patients",
    ],
    benefits: [
      "Restored deep sleep",
      "Reduced daytime sleepiness",
      "Lower cardiovascular risk",
      "Non-invasive & comfortable",
      "Flexible rental for trial",
    ],
    situations: [
      "Nightly CPAP therapy",
      "Post-sleep-study titration",
      "Trial before purchase",
      "Travel CPAP rentals",
    ],
    howWeHelp: "Doctor's prescription confirms pressure setting. We deliver the CPAP with the right mask (nasal, pillow or full-face) and humidifier. Family / patient is briefed on setup and cleaning.",
    process: [
      { step: "01", title: "Prescription", desc: "CPAP pressure setting from doctor confirmed." },
      { step: "02", title: "Delivery", desc: "CPAP with mask & humidifier delivered." },
      { step: "03", title: "Setup", desc: "Mask fitting & operation training." },
      { step: "04", title: "Support", desc: "24×7 issue support & mask replacement." },
    ],
    whyChoose: ["Medical-grade CPAP", "Mask fitting included", "Humidifier included", "Trial before purchase", "24×7 support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Do I need a sleep study before CPAP?", a: "Yes — a sleep study (polysomnography) confirms sleep apnoea and the pressure setting your CPAP needs." },
      { q: "How long does mask fitting take?", a: "About 20–30 minutes. Sometimes 2–3 mask trials are needed to find the right fit." },
      { q: "Can I rent CPAP for travel?", a: "Yes — short-term rentals from 3 days upwards are supported." },
    ],
    related: ["bipap-rental", "oxygen-therapy", "oxygen-concentrator-rental"],
    features: ["Medical CPAP", "Mask + humidifier", "Trial rentals", "Travel-friendly", "24×7 support"],
    who: "Diagnosed obstructive sleep apnoea patients.",
    description: "CPAP therapy on rent across Delhi NCR — mask fitting, humidifier and 24×7 support included.",
  },
  {
    slug: "suction-machine-rental",
    title: "Suction Machine Rental",
    category: "equipment",
    icon: "Droplets",
    short: "Electric suction apparatus for airway clearance at home.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Suction Machine on Rent in Delhi NCR | Airway Suction — KinFlex",
    metaDescription: "Rent electric suction machines in Delhi NCR for airway secretions clearance — tracheostomy, bedridden, palliative use. Same-day delivery.",
    intro: "Suction machines clear airway secretions safely at home — essential for tracheostomy, bedridden and neuro patients who can't cough effectively. KinFlex rents medical-grade electric suction machines with sterile jars, catheters and 24×7 support.",
    whoNeeds: [
      "Tracheostomy patients",
      "Bedridden and neuro patients",
      "Post-stroke dysphagia patients",
      "Palliative and end-of-life patients",
      "Post-op patients with excess secretions",
    ],
    benefits: [
      "Safer airway management at home",
      "Reduced aspiration risk",
      "Better sleep for patient",
      "Family peace of mind",
      "Portable & easy to use",
    ],
    situations: [
      "Regular tracheostomy suction",
      "Emergency airway clearance",
      "Post-op secretion management",
      "Palliative comfort suction",
    ],
    howWeHelp: "Delivery & setup at home. Family is trained on safe suction technique. Sterile catheters and jars are replaced periodically. Any issue — we service quickly.",
    process: [
      { step: "01", title: "Requirement", desc: "Confirm suction needs & frequency." },
      { step: "02", title: "Delivery", desc: "Machine, catheters & jars delivered." },
      { step: "03", title: "Training", desc: "Family trained on safe technique." },
      { step: "04", title: "Support", desc: "Consumables restocked & 24×7 support." },
    ],
    whyChoose: ["Medical-grade suction", "Sterile catheters", "Same-day delivery", "Family training", "24×7 support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "How often should suction be done?", a: "Depends on secretions. Tracheostomy patients often need suction every 2–6 hours." },
      { q: "Are catheters reusable?", a: "No — catheters are single-use sterile. We restock periodically." },
      { q: "Can family members do the suction?", a: "Yes — we train family on safe technique. Sterile suction ideally by trained caregivers or nurses." },
    ],
    related: ["tracheostomy-care", "medical-equipment-rental", "oxygen-therapy", "palliative-care"],
    features: ["Electric suction", "Sterile catheters & jars", "Same-day delivery", "Family training", "24×7 support"],
    who: "Tracheostomy, bedridden, neuro and palliative patients.",
    description: "Medical suction machines on rent — sterile, home-friendly, with family training and 24×7 support.",
  },
  {
    slug: "wheelchair-rental",
    title: "Wheelchair Rental",
    category: "equipment",
    icon: "Accessibility",
    short: "Standard, reclining and commode wheelchairs on rent.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Wheelchair on Rent in Delhi NCR | Standard, Reclining, Commode — KinFlex",
    metaDescription: "Rent wheelchairs in Delhi NCR — standard, reclining and commode types. Same-day delivery, sanitised units, flexible plans.",
    intro: "The right wheelchair transforms mobility, safety and quality of life. KinFlex rents standard folding wheelchairs, reclining wheelchairs (for long sitting), and commode wheelchairs (with toilet function) across Delhi NCR.",
    whoNeeds: [
      "Elderly patients with mobility issues",
      "Post-op and post-stroke patients",
      "Paralysis and neuro patients",
      "Long-term bedridden patients (for occasional transfer)",
      "Temporary mobility needs post-injury",
    ],
    benefits: [
      "Mobility and independence",
      "Safer patient transfers",
      "Commode option = fewer bathroom trips",
      "Reclining option = long-sitting comfort",
      "Flexible rental duration",
    ],
    situations: [
      "Post-op mobility",
      "Elderly outdoor mobility",
      "Bedridden patient transfers",
      "Long-sit rehab sessions",
      "Emergency wheelchair need",
    ],
    howWeHelp: "Choose the type — standard, reclining or commode. We deliver, adjust for patient size, brief on safety, and pick up when done.",
    process: [
      { step: "01", title: "Choose Type", desc: "Standard / reclining / commode." },
      { step: "02", title: "Delivery", desc: "Same-day delivery & adjustment." },
      { step: "03", title: "Family Brief", desc: "Safety & folding briefed." },
      { step: "04", title: "Pickup", desc: "Free pickup when rental ends." },
    ],
    whyChoose: ["Standard / reclining / commode", "Same-day delivery", "Sanitised units", "Free pickup", "Adjustable & comfortable"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "What's a commode wheelchair?", a: "A wheelchair with a built-in toilet seat and removable pot — useful for bedridden patients who need bathroom help." },
      { q: "How is a reclining wheelchair different?", a: "The backrest reclines to near-flat, useful for long sitting periods and elderly patients." },
      { q: "Can I rent for just a few days?", a: "Yes — from 1 day upwards." },
    ],
    related: ["medical-equipment-rental", "hospital-bed-rental", "elder-care", "physiotherapy-at-home"],
    features: ["Standard / reclining / commode", "Same-day delivery", "Adjustable fit", "Sanitised units", "Free pickup"],
    who: "Elderly, post-op, paralysis, bedridden patients needing mobility.",
    description: "Standard, reclining and commode wheelchairs on rent — same-day delivery across Delhi NCR.",
  },
  {
    slug: "patient-monitor-rental",
    title: "Patient Monitor Rental",
    category: "equipment",
    icon: "HeartPulse",
    short: "5-parameter patient monitor on rent — ECG, SpO₂, NIBP, temp, respiration.",
    hero: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=1600&auto=format&fit=crop",
    metaTitle: "Patient Monitor on Rent in Delhi NCR | 5-Para Monitor — KinFlex",
    metaDescription: "Rent 5-para patient monitors in Delhi NCR — ECG, SpO₂, NIBP, temperature, respiration. Perfect for home ICU and critical care setups.",
    intro: "Multi-parameter patient monitors track vitals continuously — critical for home ICU setups, ventilator-dependent patients, and post-op recovery. KinFlex rents 5-para monitors (ECG, SpO₂, NIBP, temperature, respiration) with same-day delivery and setup.",
    whoNeeds: [
      "Home ICU setups",
      "Ventilator-dependent patients",
      "High-risk post-op patients at home",
      "Cardiac patients needing continuous monitoring",
      "Palliative patients with clinical fluctuations",
    ],
    benefits: [
      "Continuous vitals monitoring",
      "Early complication detection",
      "Family peace of mind",
      "Trained nurse interpretation",
      "Flexible rental duration",
    ],
    situations: [
      "Home ICU setup",
      "Post-op high-risk monitoring",
      "Ventilator patient monitoring",
      "Cardiac observation",
      "Palliative comfort monitoring",
    ],
    howWeHelp: "Monitor is delivered, installed, and calibrated. Family and nurses are briefed on reading the display and alarms. Any issues — we service quickly.",
    process: [
      { step: "01", title: "Requirement", desc: "Confirm parameters needed." },
      { step: "02", title: "Delivery", desc: "5-para monitor delivered & calibrated." },
      { step: "03", title: "Training", desc: "Family & nurse briefed on display & alarms." },
      { step: "04", title: "Support", desc: "24×7 support & consumables restocking." },
    ],
    whyChoose: ["ICU-grade 5-para monitors", "Same-day delivery", "Calibrated & sanitised", "Family & nurse training", "24×7 support"],
    coverage: "Delhi, Noida, Greater Noida, Ghaziabad, Gurugram and Faridabad.",
    faqs: [
      { q: "Do I need a nurse to interpret the monitor?", a: "For continuous monitoring in home ICU or ventilator patients — strongly yes. For post-op observation, family training is often enough." },
      { q: "Are consumables (ECG electrodes) included?", a: "Basic consumables are included; additional restocking is charged at cost." },
      { q: "Can the monitor be moved between rooms?", a: "Yes — most units are portable with battery backup." },
    ],
    related: ["icu-setup-at-home", "critical-care-nurse", "ventilator-care", "medical-equipment-rental"],
    features: ["ECG + SpO₂ + NIBP + temp + resp", "Same-day delivery", "ICU-grade", "Family training", "24×7 support"],
    who: "Home ICU, ventilator, post-op and palliative patients needing continuous monitoring.",
    description: "5-parameter ICU-grade patient monitors on rent — continuous vitals for home ICU and critical care.",
  },
];
